import {
  autoUpdate,
  flip,
  offset,
  Placement,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { createContext, useContext, useMemo, useState } from "react";

export type PopoverTrigger = "hover" | "click";

export interface PopoverOptions {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: PopoverTrigger;
  dismissOnOutsidePress?: boolean;
  disableFlip?: boolean;
  offset?: number;
  viewportPadding?: number;
}

export function usePopover({
  initialOpen = false,
  placement = "top",
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  trigger = "hover",
  dismissOnOutsidePress = true,
  disableFlip = false,
  offset: offsetValue = 8,
  viewportPadding = 8,
}: PopoverOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const [previousControlledOpen, setPreviousControlledOpen] =
    useState(controlledOpen);
  if (controlledOpen !== previousControlledOpen) {
    setPreviousControlledOpen(controlledOpen);
    if (controlledOpen === false) {
      setUncontrolledOpen(false);
    }
  }

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      ...(disableFlip
        ? []
        : [
            flip({
              fallbackAxisSideDirection: "start",
              crossAxis: placement.includes("-"),
            }),
          ]),
      shift({ padding: viewportPadding }),
      size({
        padding: viewportPadding,
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
    ],
  });

  const hover = useHover(data.context, {
    move: false,
    enabled: controlledOpen == null && trigger === "hover",
    handleClose: safePolygon(),
    delay: { open: 400, close: 150 },
  });
  const click = useClick(data.context, {
    enabled: controlledOpen == null && trigger === "click",
  });
  const dismiss = useDismiss(data.context, {
    outsidePress: dismissOnOutsidePress,
  });

  const interactions = useInteractions([hover, click, dismiss]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data],
  );
}

type PopoverContextType = ReturnType<typeof usePopover> | null;

export const PopoverContext = createContext<PopoverContextType>(null);

export const usePopoverContext = () => {
  const context = useContext(PopoverContext);

  if (context == null) {
    throw new Error("Popover components must be wrapped in <Popover />");
  }

  return context;
};
